// useAppData — read-only Data view state for one app: its tenant tables and a
// paginated window of the selected table's rows. Plain useState/useEffect with a
// `cancelled` guard (the codebase convention — no react-query), mirroring
// Dashboard's fetch-on-mount + tri-state. All reads go through the owner-only
// /apps/:slug/data/* endpoints; errors surface as strings for the panel to show.

import { useCallback, useEffect, useState } from "react";
import { listTables, listRows, insertRow, updateRow, deleteRow, type DbTable, type RowPage } from "../api";

export const PAGE_SIZE = 50;

export type UseAppData = {
  tables:      DbTable[] | null;   // null while loading
  tablesError: string | null;
  selected:    string | null;      // selected table name
  select:      (table: string) => void;
  page:        RowPage | null;     // current page of the selected table
  rowsLoading: boolean;
  rowsError:   string | null;
  offset:      number;
  setOffset:   (offset: number) => void;
  reload:      () => void;         // refetch the current page
  // Writes on the selected table — resolve on success (page auto-refetches),
  // reject with the server error message on failure (caller shows it).
  insert:      (row: Record<string, unknown>) => Promise<void>;
  update:      (key: Record<string, unknown>, set: Record<string, unknown>) => Promise<void>;
  remove:      (key: Record<string, unknown>) => Promise<void>;
};

export function useAppData(slug: string): UseAppData {
  const [tables, setTables]           = useState<DbTable[] | null>(null);
  const [tablesError, setTablesError] = useState<string | null>(null);
  const [selected, setSelected]       = useState<string | null>(null);
  const [offset, setOffset]           = useState(0);
  const [page, setPage]               = useState<RowPage | null>(null);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError]     = useState<string | null>(null);
  const [nonce, setNonce]             = useState(0);   // manual refresh of the page

  // Tables: load once per app. Auto-select the first table so the panel opens
  // with data rather than an empty chooser.
  useEffect(() => {
    let cancelled = false;
    setTables(null); setTablesError(null); setSelected(null); setPage(null); setOffset(0);
    listTables(slug)
      .then((r) => {
        if (cancelled) return;
        setTables(r.tables);
        if (r.tables.length) setSelected(r.tables[0].name);
      })
      .catch((e) => { if (!cancelled) setTablesError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [slug]);

  // Selecting a different table always restarts at the first page.
  const select = useCallback((table: string) => {
    setSelected(table);
    setOffset(0);
  }, []);

  // Rows: (re)load whenever the table, page offset, or refresh nonce changes.
  useEffect(() => {
    if (!selected) { setPage(null); return; }
    let cancelled = false;
    setRowsLoading(true); setRowsError(null);
    listRows(slug, selected, offset, PAGE_SIZE)
      .then((r) => { if (!cancelled) setPage(r); })
      .catch((e) => { if (!cancelled) { setRowsError(String(e?.message ?? e)); setPage(null); } })
      .finally(() => { if (!cancelled) setRowsLoading(false); });
    return () => { cancelled = true; };
  }, [slug, selected, offset, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // Writes target the currently-selected table; on success bump the nonce so the
  // page refetches (and picks up total/row changes). Errors propagate to caller.
  const requireTable = () => {
    if (!selected) throw new Error("no table selected");
    return selected;
  };
  const insert = useCallback(async (row: Record<string, unknown>) => {
    await insertRow(slug, requireTable(), row); setNonce((n) => n + 1);
  }, [slug, selected]);
  const update = useCallback(async (key: Record<string, unknown>, set: Record<string, unknown>) => {
    await updateRow(slug, requireTable(), key, set); setNonce((n) => n + 1);
  }, [slug, selected]);
  const remove = useCallback(async (key: Record<string, unknown>) => {
    await deleteRow(slug, requireTable(), key); setNonce((n) => n + 1);
  }, [slug, selected]);

  return { tables, tablesError, selected, select, page, rowsLoading, rowsError, offset, setOffset, reload, insert, update, remove };
}
